/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-02-09 08:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { Modal, Layout, DisplayText, Stack, Banner, List } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';

import { getFormValues } from 'redux-form';

import StrategyDetail from './strategy-detail';
import { isTypeAllRule, validateDataBeforeSave } from 'src/helpers/merchandising-v2-helper';
import { displayDateRange } from 'src/helpers/date-time-helper';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import ProductDetail from 'src/components/views/merchandising-v2/rule/strategy/product-detail';

type Props = {
  status: String,
  active: boolean,
  loading: Boolean,
  disabled: Boolean,
  handleCancel: () => void,
  handleSave: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  validated: boolean,
  validateValues: Array<string>
};

class SaveAndPublishModal extends Component<ComposedProps, State> {
  state = {
    validated: false,
    validateValues: []
  };
  async componentWillUpdate(nextProps, nextState) {
    const { active } = this.props;
    const { active: nextActive, formValues, ruleType, i18n } = nextProps;
    if (!active && nextActive) {
      // Validate form when open modal
      const validateValues = validateDataBeforeSave(ruleType, formValues, i18n);
      this.setState({ validated: true, validateValues });
    }

    if (active && !nextActive) {
      // Reset form when close modal
      this.setState({ validated: false, validateValues: [] });
    }
  }

  sortByTypeStrategy = (strategies: Array<Object>) => {
    const objPosition = {
      pin: 1,
      boost: 2,
      demote: 3,
      hide: 4,
      filter: 5
    };
    strategies.sort((a, b) => {
      const positionA = objPosition[a.type];
      const positionB = objPosition[b.type];
      return positionA - positionB;
    });
  };
  render() {
    const {
      i18n,
      active,
      ruleType,
      formValues,
      optionsList,
      status,
      appLanguage,
      saving,
      timezone,
      hasProductPerformanceRanking,
      duplicateTerm = {}
    } = this.props;
    const { validated, validateValues } = this.state;

    const {
      queries = [],
      isEnablePerformanceRanking,
      collection,
      effectFrom,
      effectTo,
      queriesType = '',
      products = {},
      strategies = []
    } = formValues;

    const textError = i18n.translate(
      'AdminHTML.MerchandisingV2.Helper.validateConditionQuery_Existed'
    );
    const singularError = i18n.translate(
      'AdminHTML.MerchandisingV2.Helper.validateSingularQuery_Existed'
    );
    queries
      .filter(q => q)
      .forEach(query => {
        if (duplicateTerm[query] && duplicateTerm[query].isExists) {
          const error =
            duplicateTerm[query].type === 'duplicated_singular' ? singularError : textError;
          validateValues.push(error);
        }
      });
    const renderQueries = () => {
      if (isTypeAllRule(queriesType)) {
        return (
          <p className="Feature-Title-Rule">
            {`${i18n.translate('AdminHTML.MerchandisingV2.Common.applyTo')}: `}
            <span style={{ fontWeight: '600' }}>
              {i18n.translate('AdminHTML.MerchandisingV2.Rule.allQueries')}
            </span>
          </p>
        );
      } else {
        return queries.map((query, index) => (
          <p key={index} className="Feature-Title-Rule">
            {`${i18n.translate('AdminHTML.MerchandisingV2.Rule.card1_Field3_SubLabel')} `}{' '}
            <b>{query}</b>
          </p>
        ));
      }
    };

    const renderCollections = () => {
      if (isTypeAllRule(queriesType)) {
        return (
          <p className="Feature-Title-Rule">
            {`${i18n.translate('AdminHTML.MerchandisingV2.Common.applyTo')}: `}
            <span style={{ fontWeight: '600' }}>
              {i18n.translate('AdminHTML.MerchandisingV2.Rule.allCollect')}
            </span>
          </p>
        );
      } else {
        return (
          <p className="Feature-Title-Rule">
            {`${i18n.translate('AdminHTML.MerchandisingV2.ConditionsList.a_collection')} `}{' '}
            <b>
              {_.get(collection, 'label', null)
                ? _.get(collection, 'label', '')
                : _.get(collection, 'value', '')}
            </b>
          </p>
        );
      }
    };

    const productStrategies = Object.keys(products)
      .filter(type => products[type].some(v => v))
      .map(type => ({
        type,
        isProduct: true,
        products: products[type].filter(v => v)
      }));
    const allStrategies = [...strategies, ...productStrategies];
    this.sortByTypeStrategy(allStrategies);

    if (isEnablePerformanceRanking && queries.length > 0) {
      const iQueries = queries.filter(q => !_.isEmpty(q));
      allStrategies.push({
        type: 'productPerformanceRanking',
        queries: iQueries,
        index: allStrategies.length
      });
    }

    const hasDateRange =
      effectFrom !== '' && effectFrom !== undefined && effectTo !== '' && effectTo !== undefined;
    return (
      <Modal
        open={active}
        onClose={this.handleCancel}
        title={
          status === 'enabled'
            ? i18n.translate('AdminHTML.MerchandisingV2.SaveAndPublishModal.titlePublish')
            : i18n.translate('AdminHTML.MerchandisingV2.SaveAndPublishModal.titleDraft')
        }
        primaryAction={{
          content:
            status === 'enabled'
              ? i18n.translate('AdminHTML.Common.Action.publish')
              : i18n.translate('AdminHTML.Common.Action.saveDraft'),
          loading: saving,
          disabled: !validated || !_.isEmpty(validateValues),
          onAction: this.handleSave
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleCancel
          }
        ]}>
        <Modal.Section>
          <div className="BC-MerchandisingV2-Rule-save-modal">
            <Layout>
              {!_.isEmpty(validateValues) && (
                <Layout.Section>
                  <Banner
                    title={i18n.translate(
                      'AdminHTML.MerchandisingV2.SaveAndPublishModal.validate_title',
                      {
                        nt_error: i18n.translate(
                          'AdminHTML.MerchandisingV2.SaveAndPublishModal.error',
                          { count: validateValues.length, nt_number: validateValues.length }
                        )
                      }
                    )}
                    status="critical">
                    <List>
                      {_.map(validateValues, (item, index) => (
                        <List.Item key={index}>{item}</List.Item>
                      ))}
                    </List>
                  </Banner>
                </Layout.Section>
              )}
              <Layout.Section>
                <div>
                  <DisplayText size="large">{formValues.name}</DisplayText>
                  {hasDateRange && (
                    <p className="Summarize-Date-Range">
                      {displayDateRange(formValues.effectFrom, formValues.effectTo)} - {timezone}
                    </p>
                  )}
                </div>
              </Layout.Section>
              {/* Render text condition setup */}
              <Layout.Section>
                <h2 className="Polaris-Heading">
                  {i18n.translate('AdminHTML.MerchandisingV2.SaveAndPublishModal.rule1_title')}
                </h2>
                {ruleType === 'search' ? renderQueries() : renderCollections()}
              </Layout.Section>
              {/* Render text consequences setup */}
              <Layout.Section>
                <p className="Polaris-Heading">
                  {i18n.translate('AdminHTML.MerchandisingV2.SaveAndPublishModal.rule2_title')}
                </p>
                <div style={{ marginTop: '0.5rem' }}>
                  <Stack vertical>
                    {_.map(allStrategies, (strategy, index) => {
                      const { type, attributes = [], isProduct, products = [] } = strategy;
                      if (isProduct && products.length > 0) {
                        return (
                          <ProductDetail
                            key={`product-detail-${index}`}
                            type={type}
                            products={products}
                            viewOnly
                          />
                        );
                      }
                      const emptyAttributes = attributes.filter(
                        e => _.isEmpty(e) || e.value === undefined
                      );
                      if (type === 'pin') {
                        const { pinPositions = [] } = strategy;
                        const hasValuePin = pinPositions.find(
                          e => !_.isEmpty(e) && !_.isEmpty(e.id)
                        );
                        if (_.isEmpty(hasValuePin)) {
                          return null;
                        }
                      } else if (
                        type &&
                        type !== 'productPerformanceRanking' &&
                        emptyAttributes.length === attributes.length
                      ) {
                        return null;
                      }
                      if (
                        type === 'productPerformanceRanking' &&
                        (strategy.queries.length <= 0 || !hasProductPerformanceRanking)
                      )
                        return null;

                      return (
                        <StrategyDetail
                          key={index}
                          strategy={strategy}
                          index={index}
                          appLanguage={appLanguage}
                          optionsList={optionsList}
                        />
                      );
                    })}
                  </Stack>
                </div>
              </Layout.Section>
            </Layout>
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  // Handle click close button
  handleCancel = () => {
    this.props.handleCancel();
  };

  // Handle click  save data
  handleSave = () => {
    this.props.handleSaveRule();
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('MerchandisingV2RuleForm')(state);
  const optionsList = _.get(state.filterData.productAttributes, 'optionsList', []);

  return {
    formValues,
    optionsList,
    loading: _.get(state.merchandisingV2, 'loading', true),
    saving: _.get(state.merchandisingV2, 'saving', false),
    timezone: _.get(state.oauth.tenantApp, 'externalInfo.timezone', ''),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    duplicateTerm: _.get(state.merchandisingV2, 'duplicateTerm', {})
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SaveAndPublishModal));
